/**
 * Link to Trip Dialog
 * Allows user to link a document to a specific trip
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Ship, Loader2, Search, X, Link2, Unlink } from 'lucide-react';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LinkTripDialog = ({ document, onSuccess, onClose }) => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/trips`);
        setTrips(response.data.trips || []);
      } catch (err) {
        toast.error('Failed to load trips');
      } finally {
        setLoading(false);
      }
    };
    fetchTrips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = trips.filter(t => {
    const q = search.toLowerCase();
    return (
      (t.vessel_name || '').toLowerCase().includes(q) ||
      (t.trip_name || '').toLowerCase().includes(q) ||
      (t.contractor || '').toLowerCase().includes(q)
    );
  });

  const handleLink = async (tripId) => {
    setLinking(true);
    try {
      await axios.put(`${API_URL}/api/documents/${document.id}/link`, { trip_id: tripId });
      toast.success('Document linked to trip');
      onSuccess?.();
    } catch (err) {
      toast.error('Failed to link document');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async () => {
    setLinking(true);
    try {
      await axios.put(`${API_URL}/api/documents/${document.id}/link`, { trip_id: null });
      toast.success('Document unlinked');
      onSuccess?.();
    } catch (err) {
      toast.error('Failed to unlink document');
    } finally {
      setLinking(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }); } catch (err) { return d; }
  };

  return (
    <DialogContent className="bg-slate-800 border-slate-700 max-w-lg max-h-[80vh] flex flex-col" data-testid="link-trip-dialog">
      <DialogHeader>
        <DialogTitle className="text-white flex items-center gap-2">
          <Link2 className="h-5 w-5 text-amber-400" />
          Link to Trip
        </DialogTitle>
        <p className="text-xs text-slate-400 mt-1">
          {document?.file_name}
        </p>
      </DialogHeader>

      {/* Currently linked */}
      {document?.linked_trip_id && (
        <div className="flex items-center justify-between p-3 bg-amber-600/10 border border-amber-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-sm">
            <Ship className="h-4 w-4 text-amber-400" />
            <span className="text-amber-300">
              Linked to: {document.linked_trip?.vessel_name || 'Trip'}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleUnlink}
            disabled={linking}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 text-xs"
            data-testid="unlink-btn"
          >
            <Unlink className="h-3.5 w-3.5 mr-1" />
            Unlink
          </Button>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <Input
          placeholder="Search by vessel, trip name, or contractor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-slate-900/50 border-slate-600 text-white pl-10 h-9"
          data-testid="trip-search-input"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Trip list */}
      <div className="flex-1 overflow-y-auto space-y-1 min-h-0 max-h-[40vh]">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 text-amber-500 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-slate-500 py-8 text-sm">No trips found</p>
        ) : (
          filtered.map(trip => {
            const isLinked = document?.linked_trip_id === trip.id;
            return (
              <button
                key={trip.id}
                onClick={() => !isLinked && handleLink(trip.id)}
                disabled={linking || isLinked}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  isLinked
                    ? 'bg-amber-600/10 border border-amber-500/30 cursor-default'
                    : 'bg-slate-900/30 border border-slate-700/50 hover:border-amber-500/30 hover:bg-slate-700/50'
                }`}
                data-testid={`link-trip-${trip.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <Ship className={`h-4 w-4 shrink-0 ${isLinked ? 'text-amber-400' : 'text-slate-500'}`} />
                    <span className={`font-medium truncate text-sm ${isLinked ? 'text-amber-300' : 'text-white'}`}>
                      {trip.vessel_name || trip.trip_name || 'Unnamed Trip'}
                    </span>
                    {trip.trip_type === 'work_trip' && (
                      <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-[10px] shrink-0">
                        {trip.contractor || 'Work'}
                      </Badge>
                    )}
                  </div>
                  {isLinked && (
                    <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px]">Linked</Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 pl-6">
                  {trip.date_joined_vessel && trip.date_left_vessel && (
                    <span>{formatDate(trip.date_joined_vessel)} — {formatDate(trip.date_left_vessel)}</span>
                  )}
                  {trip.tax_year && <span>{trip.tax_year}</span>}
                </div>
              </button>
            );
          })
        )}
      </div>
    </DialogContent>
  );
};

export default LinkTripDialog;
